import React, { useState, useEffect } from 'react';
import { getData, saveData, db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';

const BRAND = '#0f6e56';

// Parties = Customers (debtors) and Suppliers (creditors)
export default function Parties({ user }) {
  const [parties, setParties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('customer');
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ type: 'customer', name: '', contactPerson: '', email: '', phone: '', address: '', city: '', country: '', openingBalance: '' });

  const load = async () => {
    setLoading(true);
    const [p, inv] = await Promise.all([getData(user.uid, 'parties'), getData(user.uid, 'invoices')]);
    setParties(p);
    setInvoices(inv);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    await saveData(user.uid, 'parties', {
      ...form,
      openingBalance: parseFloat(form.openingBalance) || 0,
      head: form.type === 'customer' ? 'Sundry Debtors' : 'Sundry Creditors',
      group: form.type === 'customer' ? 'ASSET' : 'LIABILITY',
    });
    setShowForm(false);
    setForm({ type: tab, name: '', contactPerson: '', email: '', phone: '', address: '', city: '', country: '', openingBalance: '' });
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this party?')) return;
    await deleteDoc(doc(db, 'users', user.uid, 'parties', id));
    load();
  };

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Calculate outstanding balance per party from invoices
  const partyBalance = (party) => {
    const invType = party.type === 'customer' ? 'sales' : 'purchase';
    const field = party.type === 'customer' ? 'customerName' : 'supplierName';
    const unpaid = invoices.filter(i => i.type === invType && i[field] === party.name && i.status === 'unpaid');
    return unpaid.reduce((s, i) => s + i.amount, 0) + (party.openingBalance || 0);
  };

  const filtered = parties
    .filter(p => p.type === tab)
    .filter(p => (p.name || '').toLowerCase().includes(search.toLowerCase()) || (p.phone || '').includes(search) || (p.email || '').toLowerCase().includes(search.toLowerCase()));

  const isCustomer = tab === 'customer';
  const accent = isCustomer ? BRAND : '#a32d2d';

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Customers & Suppliers</h2>
        <button onClick={() => { setForm(f => ({ ...f, type: tab })); setShowForm(true); }} style={{ padding: '10px 20px', background: accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ New {isCustomer ? 'Customer' : 'Supplier'}</button>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Customers are Sundry Debtors (receivable) · Suppliers are Sundry Creditors (payable)</div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden', width: 'fit-content' }}>
        {[{ id: 'customer', label: '📥 Customers (Debtors)' }, { id: 'supplier', label: '📤 Suppliers (Creditors)' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '11px 24px', border: 'none', cursor: 'pointer', fontSize: 14,
            fontWeight: tab === t.id ? 600 : 400,
            background: tab === t.id ? (t.id === 'customer' ? BRAND : '#a32d2d') : '#fff',
            color: tab === t.id ? '#fff' : '#666',
            borderRight: '1px solid #e8e8e8'
          }}>{t.label}</button>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`🔍 Search ${isCustomer ? 'customers' : 'suppliers'}...`}
        style={{ width: '100%', maxWidth: 360, padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, outline: 'none', marginBottom: 16 }} />

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{isCustomer ? '👥' : '🏭'}</div>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#aaa' }}>No {isCustomer ? 'customers' : 'suppliers'} yet</div>
          <div style={{ fontSize: 13, color: '#ccc', marginTop: 4 }}>Click "+ New {isCustomer ? 'Customer' : 'Supplier'}" to add one</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f7f8fa' }}>
              {['Name', 'Contact', 'Phone', 'Location', 'Outstanding', ''].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: h === 'Outstanding' ? 'right' : 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map(p => {
                const bal = partyBalance(p);
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>{p.head}</div>
                    </td>
                    <td style={td}>
                      {p.contactPerson || '—'}
                      {p.email && <div style={{ fontSize: 11, color: BRAND }}>{p.email}</div>}
                    </td>
                    <td style={td}>
                      {p.phone ? <a href={`https://wa.me/${p.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" style={{ color: '#25d366', textDecoration: 'none' }}>{p.phone}</a> : '—'}
                    </td>
                    <td style={td}>{[p.city, p.country].filter(Boolean).join(', ') || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: bal > 0 ? accent : '#aaa' }}>{fmt(bal)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => del(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: 15 }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <PartyForm form={form} setForm={setForm} onSubmit={add} onClose={() => setShowForm(false)} accent={accent} />}
    </div>
  );
}

// Reusable party form — also used as quick-add from invoice screen
export function PartyForm({ form, setForm, onSubmit, onClose, accent = BRAND }) {
  const isCustomer = form.type === 'customer';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 460, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>New {isCustomer ? 'Customer' : 'Supplier'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
          Will be created under {isCustomer ? 'Sundry Debtors (Receivable)' : 'Sundry Creditors (Payable)'}
        </div>
        <form onSubmit={onSubmit}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button type="button" onClick={() => setForm(f => ({ ...f, type: 'customer' }))} style={{ flex: 1, padding: 11, borderRadius: 10, border: `1.5px solid ${form.type === 'customer' ? BRAND : '#ddd'}`, background: form.type === 'customer' ? BRAND : '#fff', color: form.type === 'customer' ? '#fff' : '#555', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Customer</button>
            <button type="button" onClick={() => setForm(f => ({ ...f, type: 'supplier' }))} style={{ flex: 1, padding: 11, borderRadius: 10, border: `1.5px solid ${form.type === 'supplier' ? '#a32d2d' : '#ddd'}`, background: form.type === 'supplier' ? '#a32d2d' : '#fff', color: form.type === 'supplier' ? '#fff' : '#555', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Supplier</button>
          </div>
          <PF label={`${isCustomer ? 'Customer' : 'Supplier'} name *`} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. ABC Traders" />
          <PF label="Contact person" value={form.contactPerson} onChange={v => setForm(f => ({ ...f, contactPerson: v }))} placeholder="e.g. John Smith" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <PF label="Email" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="email@example.com" />
            <PF label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="+1 555 000 0000" />
          </div>
          <PF label="Address" value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} placeholder="Street address" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <PF label="City" value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} placeholder="City" />
            <PF label="Country" value={form.country} onChange={v => setForm(f => ({ ...f, country: v }))} placeholder="Country" />
          </div>
          <PF label="Opening balance" type="number" value={form.openingBalance} onChange={v => setForm(f => ({ ...f, openingBalance: v }))} placeholder="0.00" />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" style={{ flex: 2, padding: 12, background: accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Save {isCustomer ? 'Customer' : 'Supplier'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PF({ label, type = 'text', value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none' }} />
    </div>
  );
}

const td = { padding: '12px 16px', fontSize: 13, color: '#444' };
